import { IState } from '../reducers';
import { IPlatformConfiguration } from '../entities/IPlatformConfiguration';
import { getRuntimeClientLogo } from './runtimeInstance';

export const resolvePlatformFeatureFlag = (apiValue: boolean | undefined, legacyValue: boolean): boolean =>
    apiValue ?? legacyValue;

const selectPlatformConfiguration = (state: IState): IPlatformConfiguration | null =>
    state.config.platformConfiguration;

export const selectDelegationsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isDelegationsEnabled,
        false
    );

export const selectArchivizationEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isArchivizationEnabled,
        true
    );

export const selectGroupsInValidationRequestsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isGroupsInValidationRequestsEnabled,
        state.config.premiumFeatures?.isGroupInValidationRequestFeatureEnabled ?? false
    );

export const selectCampaignAcknowledgmentEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isCampaignAcknowledgmentEnabled,
        false
    );

export const selectSearchInSystemGroupsInMentionComponentEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isSearchInSystemGroupsInMentionComponentEnabled,
        false
    );

export const selectRegistrationFinalScreenEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isRegistrationFinalScreenEnabled,
        state.config.finalRegistration ?? false
    );

export const selectWopiEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isWopiIntegrationEnabled,
        state.config.isWopiIntegrationActive ?? false
    );

export const selectNormsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isNormsEnabled,
        true
    );

export const selectSignatureEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isSignatureEnabled,
        true
    );

export const selectCampaignEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isCampaignEnabled,
        true
    );

export const selectApprovalEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isApprovalEnabled,
        true
    );

export const selectNewsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.featureFlags?.isNewsEnabled,
        true
    );

export const selectTrackingEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.basicConfiguration?.isTrackingEnabled,
        false
    );

export const selectGtmEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.basicConfiguration?.isGtmEnabled,
        false
    );

export const selectStonlyEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.basicConfiguration?.isStonlyEnabled,
        false
    );

export const selectSimpleLegalEntityFormEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.additionalPlatformSettings?.isSimpleLegalEntityFormEnabled,
        false
    );

export const selectHomepageNewsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.additionalPlatformSettings?.isHomepageNewsEnabled,
        true
    );

export const selectPasswordSettingsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.userSettings?.isPasswordEnabled,
        true
    );

export const selectEmailSettingsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.userSettings?.isEmailEnabled,
        true
    );

export const selectNotificationSettingsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.userSettings?.isNotificationEnabled,
        true
    );

export const selectHeaderProfileEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isProfileEnabled,
        true
    );

export const selectHeaderSettingsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isSettingsEnabled,
        true
    );

export const selectHeaderTermsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isTermsEnabled,
        false
    );

export const selectHeaderPrivacyPolicyEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isPrivacyPolicyEnabled,
        true
    );

export const selectHeaderPaymentEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isPaymentEnabled,
        true
    );

export const selectHeaderSearchEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isSearchEnabled,
        true
    );

export const selectHeaderNotificationsEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isNotificationsEnabled,
        true
    );

export const selectHeaderMessagingEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.headerMenu?.isMessagingEnabled,
        true
    );

export const selectFooterEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.teamApp?.isFooterEnabled,
        false
    );

export const selectSignalREnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.teamApp?.isSignalREnabled,
        !!(state.config.appConfig?.signalRUri || state.config.signalRUri)
    );

export const selectExternalLoginEnabled = (state: IState): boolean =>
    resolvePlatformFeatureFlag(
        selectPlatformConfiguration(state)?.basicConfiguration?.isExternalLoginEnabled,
        true
    );

const resolvePlatformText = (apiValue: string | undefined, legacyValue: string): string =>
    (apiValue ?? '').trim() || legacyValue;

export const selectHelpdeskUrl = (state: IState): string =>
    resolvePlatformText(selectPlatformConfiguration(state)?.basicConfiguration?.helpdeskUrl, '');

export const selectPlatformDisplayName = (state: IState): string =>
    resolvePlatformText(selectPlatformConfiguration(state)?.basicConfiguration?.platformName, '');

export const selectPlatformOwner = (state: IState): string =>
    resolvePlatformText(selectPlatformConfiguration(state)?.basicConfiguration?.platformOwner, '');

export const selectPlatformPolicyUrl = (state: IState): string =>
    resolvePlatformText(selectPlatformConfiguration(state)?.basicConfiguration?.platformPolicyUrl, '');

export const selectAppDefaultLanguage = (state: IState): string =>
    resolvePlatformText(
        selectPlatformConfiguration(state)?.basicConfiguration?.appDefaultLanguage,
        state.config.appConfig?.defaultLanguage ?? ''
    );

export const selectDefaultOrganizationPermission = (state: IState): string =>
    resolvePlatformText(selectPlatformConfiguration(state)?.basicConfiguration?.defaultOrganizationPermission, '');

export const selectClientLogo = (state: IState): string =>
    selectPlatformConfiguration(state)?.basicConfiguration?.logo || getRuntimeClientLogo();
